<template>
  <div class="card mb-4">
    <div class="card-header pb-0">
      <div class="d-flex justify-content-between align-items-center">
        <h6 class="text-uppercase mb-0">Estudio socioeconómico</h6>
        <button
          class="btn btn-primary btn-sm"
          @click="openMessageModal"
        >
          <i class="fas fa-plus"></i> Mensaje de modal
        </button>
      </div>
    </div>
    <div class="card-body">
      <div v-if="loading" class="text-center py-4">
        <p class="mb-0">Cargando estudio...</p>
      </div>
      <div v-else-if="!study" class="text-center py-4">
        <p class="mb-0">El cliente aún no ha enviado el estudio.</p>
      </div>
      <div v-else>
        <div
          class="d-flex justify-content-between align-items-center flex-wrap mb-3"
        >
          <div>
            <span class="badge text-white" :class="statusBadgeClass">
              {{ statusLabel }}
            </span>
            <span class="text-xs text-secondary ms-2">
              Enviado el {{ $filters.formatDate(study.submitted_at) }}
            </span>
          </div>
          <div class="d-flex gap-2">
            <button
              type="button"
              class="btn btn-success btn-sm mb-0"
              @click="openDecisionModal('approved')"
            >
              Aprobar
            </button>
            <button
              type="button"
              class="btn btn-danger btn-sm mb-0"
              @click="openDecisionModal('rejected')"
            >
              Rechazar
            </button>
          </div>
        </div>

        <div v-if="study.decision_message" class="alert alert-secondary text-xs mb-3">
          <p class="mb-1 font-weight-bold">
            Decisión enviada al cliente ({{ $filters.formatDate(study.decision_at) }}):
          </p>
          <p class="mb-0">{{ study.decision_message }}</p>
        </div>

        <div class="table-responsive">
          <table class="table table-bordered table-sm">
            <tbody>
              <tr v-for="row in answerRows" :key="row.label">
                <th class="text-xs" style="width: 40%">{{ row.label }}</th>
                <td class="text-xs">{{ row.value }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Decision Modal -->
    <div class="modal" v-if="decision_modal_visible">
      <div class="modal-dialog modal-lg">
        <div
          class="modal-content"
          style="width: 60vw; max-width: 100% !important"
        >
          <div class="modal-header py-2">
            <h5 class="modal-title">
              {{
                decision_type === "approved"
                  ? "Aprobar estudio socioeconómico"
                  : "Rechazar estudio socioeconómico"
              }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeDecisionModal"
            ></button>
          </div>
          <div class="modal-body p-3">
            <label for="decision_message" class="form-label small mb-1">
              Mensaje para el cliente
            </label>
            <textarea
              id="decision_message"
              class="form-control form-control-sm"
              rows="6"
              v-model="decision_message"
            ></textarea>
            <small class="text-danger">
              Reemplace [canal oficial] antes de enviar.
            </small>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeDecisionModal"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!canSubmitDecision || submitting_decision"
              @click="submitDecision"
            >
              Enviar decisión
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Message Modal Configuration Modal -->
    <div class="modal" v-if="message_modal_visible">
      <div class="modal-dialog modal-lg">
        <div
          class="modal-content"
          style="width: 60vw; max-width: 100% !important"
        >
          <div class="modal-header py-2">
            <h5 class="modal-title">Mensaje de modal para el cliente</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeMessageModal"
            ></button>
          </div>
          <div class="modal-body p-3">
            <div class="mb-3">
              <label for="modal_message_title" class="form-label text-xs mb-1">
                Título del modal
              </label>
              <input
                id="modal_message_title"
                type="text"
                class="form-control form-control-sm"
                v-model="modal_message_title"
                placeholder="Ej: Información requerida"
              />
            </div>
            <div class="mb-3">
              <label for="modal_message_body" class="form-label text-xs mb-1">
                Mensaje
              </label>
              <textarea
                id="modal_message_body"
                class="form-control form-control-sm"
                rows="5"
                v-model="modal_message_body"
                placeholder="Escriba el mensaje que verá el cliente..."
              ></textarea>
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="show_modal"
                v-model="show_modal"
              />
              <label class="form-check-label text-xs" for="show_modal">
                <strong>Mostrar modal al cliente</strong>
                <small class="text-secondary d-block">Si está marcado, el cliente verá este mensaje al tener el estudio completo</small>
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeMessageModal"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!canSaveModalMessage || saving_modal_message"
              @click="saveModalMessage"
            >
              <span v-if="saving_modal_message">Guardando...</span>
              <span v-else>Guardar mensaje</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, post } from "@/utils/httpRequest";

const APPROVAL_TEMPLATE =
  "Su solicitud de condonación fue aprobada. Tiene 24 horas para confirmar su aceptación respondiendo a este mensaje o desde [canal oficial]. Si no la confirma en ese plazo, la solicitud se cerrará y podrá presentar una nueva. Recuerde: nunca le pediremos pagos, claves ni códigos para aprobar o activar una condonación.";

const REJECTION_TEMPLATE =
  "Su solicitud de condonación no fue aprobada con base en la información evaluada. Aún puede acceder a otras alternativas, como un acuerdo de pago o una reducción parcial. Si desea conocerlas o solicitar una revisión de su caso, responda a este mensaje o comuníquese a [canal oficial].";

export default {
  name: "SocioeconomicStudyPanel",
  props: {
    userId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      study: null,
      loading: false,
      decision_modal_visible: false,
      decision_type: "approved",
      decision_message: "",
      submitting_decision: false,
      modal_message_title: "",
      modal_message_body: "",
      show_modal: false,
      saving_modal_message: false,
      message_modal_visible: false,
      employment_type_labels: {
        employee_contract: "Tengo empleo con contrato",
        self_employed: "Trabajo por mi cuenta",
        own_business: "Tengo un negocio propio",
        pensioner: "Soy pensionado(a)",
        unemployed: "No tengo trabajo en este momento",
        other: "Otro",
      },
      employment_duration_labels: {
        lt_6m: "Menos de 6 meses",
        "6m_1y": "Entre 6 meses y 1 año",
        "1y_3y": "Entre 1 y 3 años",
        gt_3y: "Más de 3 años",
      },
      other_income_source_labels: {
        rent: "Arriendos",
        commissions: "Comisiones",
        investments: "Inversiones",
        family_support: "Ayuda de familiares",
        other: "Otro",
      },
      income_proof_document_labels: {
        employment_certificate: "Certificado laboral",
        pay_stubs: "Colillas de pago",
        bank_statements: "Extractos bancarios de los últimos 3 meses",
        tax_return: "Declaración de renta",
        accountant_certificate: "Certificado de un contador",
        none: "No tengo ningún documento",
      },
      dependents_labels: {
        0: "Ninguna",
        1: "1",
        2: "2",
        3: "3",
        "4_plus": "4 o más",
      },
      creditor_labels: {
        banks: "Bancos o tarjetas de crédito",
        individuals: "Personas (familiares, amigos, prestamistas)",
        stores: "Almacenes o comercios",
        health: "Clínicas u hospitales",
        taxes: "Impuestos (DIAN, predial, vehículo)",
        other: "Otro",
      },
      debt_arrears_labels: {
        current: "No, estoy al día",
        lt_3m: "Sí, menos de 3 meses",
        "3m_12m": "Sí, entre 3 y 12 meses",
        gt_1y: "Sí, más de 1 año",
      },
      payment_capacity_labels: {
        lump_sum: "Podría hacer un solo pago ahora",
        installments: "Podría pagar en cuotas mensuales",
        none: "En este momento no puedo pagar nada",
      },
      asset_labels: {
        house: "Casa o apartamento",
        land: "Lote o finca",
        vehicle: "Carro o moto",
        company_share: "Parte de una empresa",
        other: "Otro",
      },
      status_labels: {
        pending: "Pendiente",
        approved: "Aprobado",
        rejected: "Rechazado",
      },
      status_badge_classes: {
        pending: "bg-warning text-dark",
        approved: "bg-success",
        rejected: "bg-danger",
      },
    };
  },
  computed: {
    statusLabel() {
      if (!this.study) return "";
      return this.status_labels[this.study.status] || this.study.status;
    },
    statusBadgeClass() {
      if (!this.study) return "bg-secondary";
      return this.status_badge_classes[this.study.status] || "bg-secondary";
    },
    canSubmitDecision() {
      const message = (this.decision_message || "").trim();
      return message.length > 0 && !message.includes("[canal oficial]");
    },
    canSaveModalMessage() {
      const title = (this.modal_message_title || "").trim();
      const body = (this.modal_message_body || "").trim();
      return title.length > 0 && body.length > 0;
    },
    otherIncomeSummary() {
      const s = this.study;
      if (!s || !s.has_other_income) return "No";
      let text = this.joinLabels(
        s.other_income_sources,
        this.other_income_source_labels,
      );
      if (
        (s.other_income_sources || []).includes("other") &&
        s.other_income_source_other
      ) {
        text += ` (${s.other_income_source_other})`;
      }
      if (s.other_income_amount) {
        text += ` — ${this.formatMoney(s.other_income_amount)}`;
      }
      return text;
    },
    creditorsSummary() {
      const s = this.study;
      if (!s) return "";
      let text = this.joinLabels(s.creditors, this.creditor_labels);
      if ((s.creditors || []).includes("other") && s.creditors_other) {
        text += ` (${s.creditors_other})`;
      }
      return text;
    },
    assetsSummary() {
      const s = this.study;
      if (!s || !s.has_assets) return "No";
      let text = this.joinLabels(s.assets, this.asset_labels);
      if ((s.assets || []).includes("other") && s.assets_other) {
        text += ` (${s.assets_other})`;
      }
      return text;
    },
    paymentAmountSummary() {
      const s = this.study;
      if (!s) return "";
      if (s.payment_capacity === "lump_sum") {
        return this.formatMoney(s.lump_sum_amount);
      }
      if (s.payment_capacity === "installments") {
        return `${this.formatMoney(s.monthly_installment_amount)} / mes`;
      }
      return "No aplica";
    },
    answerRows() {
      const s = this.study;
      if (!s) return [];
      const employmentType =
        this.employment_type_labels[s.employment_type] || s.employment_type;
      return [
        {
          label: "1. ¿En qué trabaja actualmente?",
          value:
            s.employment_type === "other" && s.employment_type_other
              ? `${employmentType} (${s.employment_type_other})`
              : employmentType,
        },
        {
          label: "2. ¿Cuánto tiempo lleva en ese trabajo o actividad?",
          value:
            this.employment_duration_labels[s.employment_duration] ||
            s.employment_duration,
        },
        {
          label: "3. Ingreso mensual promedio",
          value: this.formatMoney(s.monthly_income),
        },
        {
          label: "4. ¿Recibe dinero de otra parte?",
          value: this.otherIncomeSummary,
        },
        {
          label: "5. Documento para demostrar ingresos",
          value: this.joinLabels(
            s.income_proof_documents,
            this.income_proof_document_labels,
          ),
        },
        {
          label: "6. Personas que dependen de su dinero",
          value: this.dependents_labels[s.dependents] ?? s.dependents,
        },
        {
          label: "7. Gasto mensual básico",
          value: this.formatMoney(s.monthly_basic_expenses),
        },
        {
          label: "8. Deuda total",
          value: this.formatMoney(s.total_debt),
        },
        {
          label: "9. ¿A quién le debe?",
          value: this.creditorsSummary,
        },
        {
          label: "10. ¿Está atrasado en alguna deuda?",
          value: this.debt_arrears_labels[s.debt_arrears] || s.debt_arrears,
        },
        {
          label: "11. ¿Cómo podría pagar una parte?",
          value:
            this.payment_capacity_labels[s.payment_capacity] ||
            s.payment_capacity,
        },
        {
          label: "12. ¿Cuánto podría pagar?",
          value: this.paymentAmountSummary,
        },
        {
          label: "13. Bienes a su nombre",
          value: this.assetsSummary,
        },
        {
          label: "14. Confirmación",
          value: s.confirmed ? "Sí, confirmó" : "No",
        },
      ];
    },
  },
  watch: {
    userId: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.fetchStudy();
        }
      },
    },
  },
  methods: {
    formatMoney(value) {
      if (value === null || value === undefined || value === "") {
        return "$0";
      }
      const formatter = new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        maximumFractionDigits: 0,
      });
      return formatter.format(value);
    },
    joinLabels(values, labelMap) {
      const list = (values || []).map((value) => labelMap[value] || value);
      return list.length ? list.join(", ") : "—";
    },
    async fetchStudy() {
      if (!this.userId) return;
      this.loading = true;
      try {
        const { data } = await get(
          `socioeconomic-study/find-by-user?id=${this.userId}`,
        );
        this.study = data || null;
        // Load modal message fields
        if (this.study) {
          this.modal_message_title = this.study.modal_message_title || "";
          this.modal_message_body = this.study.modal_message_body || "";
          this.show_modal = this.study.show_modal || false;
        } else {
          this.modal_message_title = "";
          this.modal_message_body = "";
          this.show_modal = false;
        }
      } catch (error) {
        console.error("Error fetching socioeconomic study:", error);
        this.study = null;
      } finally {
        this.loading = false;
      }
    },
    openDecisionModal(type) {
      this.decision_type = type;
      this.decision_message =
        type === "approved" ? APPROVAL_TEMPLATE : REJECTION_TEMPLATE;
      this.decision_modal_visible = true;
    },
    closeDecisionModal() {
      this.decision_modal_visible = false;
      this.decision_message = "";
      this.submitting_decision = false;
    },
    async openMessageModal() {
      // Open immediately so a slow or failed refresh cannot swallow the click.
      this.message_modal_visible = true;
      await this.fetchStudy();
    },
    closeMessageModal() {
      this.message_modal_visible = false;
    },
    async submitDecision() {
      if (!this.canSubmitDecision) return;
      this.submitting_decision = true;
      try {
        await post("socioeconomic-study/decide", {
          _id: this.userId,
          status: this.decision_type,
          message: this.decision_message,
        });

        this.$swal({
          html:
            this.decision_type === "approved"
              ? "Estudio aprobado con éxito"
              : "Estudio rechazado con éxito",
          icon: "success",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });

        this.closeDecisionModal();
        await this.fetchStudy();
      } catch (error) {
        console.error("Error deciding socioeconomic study:", error);
        this.$swal({
          html: "Error al registrar la decisión del estudio",
          icon: "error",
          timer: 2500,
          showCancelButton: false,
          showConfirmButton: false,
        });
      } finally {
        this.submitting_decision = false;
      }
    },
    async saveModalMessage() {
      if (!this.canSaveModalMessage) return;
      this.saving_modal_message = true;
      try {
        await post("socioeconomic-study/save-modal-message", {
          _id: this.userId,
          modal_message_title: this.modal_message_title.trim(),
          modal_message_body: this.modal_message_body.trim(),
          show_modal: this.show_modal,
        });

        this.$swal({
          html: "Mensaje de modal guardado con éxito",
          icon: "success",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });

        await this.fetchStudy();
      } catch (error) {
        console.error("Error saving modal message:", error);
        this.$swal({
          html: "Error al guardar el mensaje de modal",
          icon: "error",
          timer: 2500,
          showCancelButton: false,
          showConfirmButton: false,
        });
      } finally {
        this.saving_modal_message = false;
      }
    },
  },
};
</script>

<style scoped>
/* Bootstrap's .modal defaults to display: none; these modals are toggled with v-if. */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
}
</style>
